import {
  useRef,
  useState,
  useLayoutEffect,
  useEffect,
  useDeferredValue,
} from "react"
import { useVirtualizer } from "@tanstack/react-virtual"
import { Card } from "./Card"
import { useCardsContext } from "./context/CardsContext"
import { useAppSizeContext } from "./context/AppSizeContext"
import { CARD_RATIO } from "./constants"

const CARD_HEIGHT = 345
const CARD_WIDTH = CARD_HEIGHT * CARD_RATIO
const GAP = 20
// phones: fixed 2-up, card size derived from the container width
const MOBILE_COLUMNS = 2
const MOBILE_GAP = 14
// rows need extra room since the INDEX button overhangs the card bottom
const MOBILE_ROW_GAP = 28

export const CardGrid = ({ cards }) => {
  // let urgent updates (typing, toggling filters) paint first and
  // re-render the grid at low priority with the new card list:
  const deferredCards = useDeferredValue(cards)
  const { isSmall } = useAppSizeContext()

  const parentRef = useRef(null)
  const [available, setAvailable] = useState(0)

  useLayoutEffect(() => {
    const el = parentRef.current
    if (!el) return
    const measure = () => {
      const cs = getComputedStyle(el)
      setAvailable(
        el.clientWidth -
          parseFloat(cs.paddingLeft) -
          parseFloat(cs.paddingRight),
      )
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const gap = isSmall ? MOBILE_GAP : GAP
  const rowGap = isSmall ? MOBILE_ROW_GAP : GAP
  let columns, cardWidth, cardHeight
  if (isSmall) {
    columns = MOBILE_COLUMNS
    cardWidth = available
      ? Math.floor((available - gap * (columns - 1)) / columns)
      : CARD_WIDTH
    cardHeight = Math.round(cardWidth / CARD_RATIO)
  } else {
    cardWidth = CARD_WIDTH
    cardHeight = CARD_HEIGHT
    columns = Math.max(1, Math.floor((available + gap) / (cardWidth + gap)))
  }

  const rowCount = Math.ceil(deferredCards.length / columns)
  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => cardHeight + rowGap,
    overscan: 2,
  })

  // row heights change when the responsive card size does
  useEffect(() => {
    virtualizer.measure()
  }, [cardHeight, rowGap, virtualizer])

  // center the grid block; cards stay left-aligned within it so
  // a partial last row lines up with the rows above
  const rowWidth = columns * (cardWidth + gap) - gap

  return (
    <div className="cards" ref={parentRef}>
      <div
        style={{
          height: virtualizer.getTotalSize(),
          position: "relative",
        }}
      >
        {virtualizer.getVirtualItems().map((row) => (
          <div
            key={row.key}
            style={{
              position: "absolute",
              top: 0,
              left: "50%",
              width: rowWidth,
              display: "flex",
              gap,
              transform: `translateX(-50%) translateY(${row.start}px)`,
            }}
          >
            {deferredCards
              .slice(row.index * columns, (row.index + 1) * columns)
              .map((c) => (
                <Card
                  card={c}
                  key={c.computed.imgUrl}
                  name={c.name}
                  tcgPrice={c.computed.tcgLowestPrice}
                  yetiPrice={c.computed.yetiLowestPrice}
                  idxUrl={c.computed.idxUrl}
                  imgUrl={c.computed?.imgUrl}
                  height={cardHeight}
                  regularPrints={c.computed.regularPrints}
                  specialPrints={c.computed.specialPrints}
                  useCardsContext={useCardsContext}
                  shadow
                />
              ))}
          </div>
        ))}
      </div>
    </div>
  )
}
